import React, { useEffect, useMemo, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { CircularProgress } from '@mui/material';
import {
  Cell,
  ColumnDef,
  ColumnResizeMode,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Header,
  HeaderGroup,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  useReactTable
} from '@tanstack/react-table';
import { MAX_TABLE_ROWS } from 'constants/constants';

import EmptyState from 'components/EmptyState/EmptyState';

import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '../ui';

import { ColumnResizeHandle } from './ColumnResizeHandle';
import DataTablePagination from './components/DataTablePagination';
import { geTableSelectionColumn, SELECT_ALL_ROWS_ID } from './utils';

export type Size = 'sm' | 'md' | 'lg';

export interface DataTableProps<T extends object> {
  data: T[];
  columns: ColumnDef<T>[];
  rowIdIdentifier?: string;
  showHeader?: boolean;
  showDataAggregatedRow?: boolean;
  aggregatedRowData?: { title: string; amount: number };
  size?: Size;
  className?: string;
  showPagination?: boolean;
  rowsPerPage?: number;
  bodyDataTestId?: string;
  isLoading?: boolean;
  emptyStateConfig?: {
    title: string;
    isEmpty: boolean;
    description: React.ReactNode;
    onClick: () => void;
  };
  bordered?: boolean;
  manualPagination?: boolean;
  pageIndex?: number;
  pageCount?: number;
  onPageChange?: (newPageIndex: number) => void;
  rowCount?: number;
  getRowId?: (row: T) => string;
  rowSelectionConfig?: RowSelectionConfig<T>;
  onRowClick?: (row: T) => void;
  enableColumnResizing?: boolean;
  stickyHeader?: boolean;
  infiniteScrollConfig?: {
    enabled: boolean;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore?: boolean;
    scrollContainerRef?: React.RefObject<HTMLElement>;
  };
}

type RowSelectionConfig<T> = {
  enableRowSelection?: boolean;
  isCheckboxesDisabled?: boolean;
  selectedRowsState?: RowSelectionState;
  onRowSelectionChange?: (selectedRows: T[]) => void;
};

const sizeClasses = {
  sm: 'h-12',
  md: 'h-16',
  lg: 'h-24'
};

const DataTable = <T extends object>({
  data = [],
  columns,
  className,
  rowIdIdentifier = '',
  showHeader = true,
  size = 'sm',
  showPagination = true,
  rowsPerPage = MAX_TABLE_ROWS,
  bodyDataTestId = '',
  isLoading = false,
  emptyStateConfig,
  bordered = false,
  manualPagination = false,
  pageIndex: controlledPageIndex = 0,
  pageCount: controlledPageCount = 0,
  onPageChange,
  rowCount,
  rowSelectionConfig,
  getRowId,
  onRowClick,
  enableColumnResizing = false,
  stickyHeader = false,
  infiniteScrollConfig
}: DataTableProps<T>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    rowSelectionConfig?.selectedRowsState || {}
  );
  const [columnSizing, setColumnSizing] = useState({});
  const { enableRowSelection, onRowSelectionChange, isCheckboxesDisabled } =
    rowSelectionConfig || {};

  useEffect(() => {
    if (rowSelectionConfig?.selectedRowsState) {
      setRowSelection(rowSelectionConfig.selectedRowsState);
    }
  }, [rowSelectionConfig?.selectedRowsState]);

  const memoizedColumns = useMemo(() => {
    if (!enableRowSelection) return columns;

    return [
      geTableSelectionColumn(isCheckboxesDisabled),
      ...columns
    ] as ColumnDef<T>[];
  }, [columns, enableRowSelection, isCheckboxesDisabled]);

  // Check if any column has sorting enabled
  const hasSortingEnabled = useMemo(() => {
    return memoizedColumns.some((column) => column.enableSorting);
  }, [memoizedColumns]);

  const memoizedData = useMemo(() => data, [data]);
  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: controlledPageIndex ?? 0,
    pageSize: rowsPerPage
  });

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue
  ) => {
    const newState: PaginationState =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(localPagination)
        : updaterOrValue;

    if (manualPagination) {
      onPageChange?.(newState.pageIndex);
    } else {
      setLocalPagination(newState);
    }
  };

  // Infinite scroll setup
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const enabled = infiniteScrollConfig?.enabled;
    const hasMore = infiniteScrollConfig?.hasMore;
    const isLoadingMore = infiniteScrollConfig?.isLoadingMore;
    const onLoadMore = infiniteScrollConfig?.onLoadMore;
    const scrollContainer = infiniteScrollConfig?.scrollContainerRef?.current;

    // Don't set up observer if not enabled, no more data, already loading, or initial load
    if (!enabled || !hasMore || isLoadingMore || isLoading || !onLoadMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        // Only trigger if intersecting and not already in a loading state
        if (target.isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: scrollContainer || null,
        rootMargin: '200px', // Increased to trigger earlier
        threshold: 0
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [
    infiniteScrollConfig?.enabled,
    infiniteScrollConfig?.hasMore,
    infiniteScrollConfig?.isLoadingMore,
    infiniteScrollConfig?.onLoadMore,
    infiniteScrollConfig?.scrollContainerRef,
    isLoading
  ]);

  const table = useReactTable({
    data: memoizedData,
    columns: memoizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...(hasSortingEnabled && { getSortedRowModel: getSortedRowModel() }),
    enableSorting: hasSortingEnabled,
    autoResetPageIndex: false,
    manualPagination,
    getRowId,
    pageCount: manualPagination ? controlledPageCount : undefined,
    ...(enableColumnResizing && {
      enableColumnResizing: true,
      columnResizeMode: 'onChange' as ColumnResizeMode,
      onColumnSizingChange: setColumnSizing,
    }),
    state: {
      rowSelection,
      pagination: manualPagination
        ? { pageIndex: controlledPageIndex!, pageSize: rowsPerPage }
        : localPagination,
      ...(enableColumnResizing && { columnSizing })
    },
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);
      if (onRowSelectionChange) {
        const selectedRows = table
          .getPrePaginationRowModel()
          .rows.filter((row) => newSelection[row.id])
          .map((row) => row.original);
        onRowSelectionChange(selectedRows);
      }
    }
  });

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-[200px]">
          <CircularProgress className="h-12" />
        </div>
      ) : emptyStateConfig?.isEmpty ? (
        <EmptyState
          title={`Add your first ${emptyStateConfig.title}`}
          description={emptyStateConfig.description}
          buttonText={`Create New ${emptyStateConfig.title}`}
          onClick={emptyStateConfig.onClick}
        />
      ) : (
        <div
          className={cn(
            'border-base-sidebarBorder rounded-lg',
            bordered ? 'border' : 'border-0',
            !stickyHeader && 'overflow-hidden'
          )}
        >
          <Table
            disableScrollWrapper={stickyHeader}
            className={cn(
              'w-full',
              enableColumnResizing ? 'table-auto' : 'table-fixed [&_td:last-child]:w-[60px] [&_td:last-child]:max-w-[60px] [&_th:last-child]:w-[60px] [&_th:last-child]:max-w-[60px]',
              className
            )}
            style={{ tableLayout: enableColumnResizing ? 'auto' : 'fixed' }}
          >
            {showHeader && (
              <TableHeader className={cn(stickyHeader && 'sticky top-0 z-50 bg-white shadow-sm')}>
                {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
                  <TableRow key={headerGroup.id} className={cn(stickyHeader && 'border-b border-base-border')}>
                    {headerGroup.headers.map((header: Header<T, any>, _) => (
                      <TableHead
                        key={header.id}
                        id={header.id}
                        className={cn(
                          'py-4 px-4 text-base-mutedForeground text-p-s font-normal text-left align-top relative',
                          'last:text-right',
                          header.id === SELECT_ALL_ROWS_ID && 'w-12',
                          header.column.columnDef.enableSorting &&
                          'cursor-pointer select-none',
                          enableColumnResizing && 'hover:border-r hover:border-base-border last:hover:border-r-0',
                          stickyHeader && 'bg-white'
                        )}
                        onClick={
                          header.column.columnDef.enableSorting
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                        style={{
                          width: enableColumnResizing ? header.getSize() : undefined
                        }}
                      >
                        <div className="flex items-center gap-2 group">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.columnDef.enableSorting && (
                            <FontAwesomeIcon
                              icon={
                                header.column.getIsSorted() === 'asc'
                                  ? 'fa-solid fa-arrow-up'
                                  : header.column.getIsSorted() === 'desc'
                                    ? 'fa-solid fa-arrow-down'
                                    : 'fa-solid fa-arrow-up'
                              }
                              className={cn(
                                'text-xs text-base-mutedForeground transition-opacity',
                                !header.column.getIsSorted() && 'opacity-0 group-hover:opacity-100'
                              )}
                            />
                          )}
                        </div>
                        {enableColumnResizing && <ColumnResizeHandle header={header} />}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            )}
            <TableBody data-testid={bodyDataTestId}>
              {table.getRowModel().rows.length ? (
                <>
                  {table.getRowModel().rows.map((row: Row<T>) => (
                    <TableRow
                      data-testid={
                        rowIdIdentifier
                          ? (row.original as Record<string, string>)[
                          rowIdIdentifier
                          ]
                          : undefined
                      }
                      key={row.id}
                      className={cn(
                        'first:border-t-0 box-border',
                        'hover:bg-base-muted',
                        onRowClick && 'cursor-pointer',
                        sizeClasses[size as Size]
                      )}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell: Cell<T, any>) => (
                        <TableCell
                          data-testid={cell.column.id}
                          key={cell.id}
                          className={cn(
                            'px-4 border-t text-left text-base-foreground text-p-s min-w-0 max-w-0',
                            'last:text-right'
                          )}
                          style={{
                            overflow: 'hidden',
                            width: enableColumnResizing ? cell.column.getSize() : undefined
                          }}
                        >
                          <div
                            className={cn(
                              'inline-flex items-center justify-start w-full',
                              'whitespace-nowrap overflow-hidden text-ellipsis max-w-full',
                              '[&::-webkit-scrollbar]:hidden'
                            )}
                            style={{
                              minWidth: 0,
                              color: 'var(--base-sidebar-foreground)'
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </div>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {infiniteScrollConfig?.enabled && infiniteScrollConfig?.hasMore && !infiniteScrollConfig?.isLoadingMore && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center py-2 border-t"
                      >
                        <div ref={loadMoreRef} style={{ height: '1px', visibility: 'hidden' }} />
                      </TableCell>
                    </TableRow>
                  )}
                  {infiniteScrollConfig?.enabled && infiniteScrollConfig?.isLoadingMore && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center py-4 border-t"
                      >
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center py-10"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {showPagination && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="px-5 text-base-foreground text-p-s"
                  >
                    <DataTablePagination table={table} rowCount={rowCount} />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      )}
    </>
  );
};

export default DataTable;
